import { preparePaymentBuyer } from '../features/cart/paymentBuyer';
import StakeholderActionButton from "./StakeholderActionButton";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  LogOut,
  RefreshCw,
  Settings,
  User,
} from "lucide-react";
import { logout } from "../features/auth/posUserSlice";
import {
  queueOrder,
  createOrder,
  fetchLatestOrders,
  publishQueuedOrdersSequential,
  fetchPOSOrders,
  fetchPOSOrderDetails,
} from "../features/orders/orderSlice";
import { initiateDeliveryPayment } from "../features/payment/paymentSlice";
import { fetchCustomerByPhone, createCustomer } from "../features/customers/customerSlice";
import { pingBackend } from "../utils/network";
import { openPrinterSettingsWindow } from "../utils/printerConfig";
import MultiPaymentModal from "../components/MultiPaymentModal";
import { clearCart } from "../features/cart/cartSlice";
import {
  fetchAllProducts,
  fetchAllProductsFresh,
} from "../features/products/productSlice";
import PhoneModal from "./PhoneModal";
import OrderFulfillmentModal from "./OrderFulfillmentModal";
import logo from "../assests/ManaKiranaLogo1024x1024.png";
import POSDispatchButtons from "./POSDispatchButtons";
import InvoiceShareModal from "./InvoiceShareModal";
import { getOrderDiscountSummary } from "../utils/orderDiscount";

function AppModal({
  open,
  title,
  message,
  type = "info",
  onClose,
  onConfirm,
  confirmText = "OK",
  cancelText = "Cancel",
  showCancel = false,
  children,
  size = "max-w-md",
}) {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => {
      const focusable = modalRef.current?.querySelectorAll(
        'input, button, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      const focusableEls = Array.from(focusable || []).filter(
        (el) => !el.disabled
      );

      if (focusableEls.length > 0) {
        const preferredInput = focusableEls.find(
          (el) =>
            el.tagName?.toLowerCase() === "input" ||
            el.tagName?.toLowerCase() === "textarea" ||
            el.tagName?.toLowerCase() === "select"
        );

        (preferredInput || focusableEls[0]).focus();
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [open]);

  if (!open) return null;

  const accent =
    type === "error"
      ? "bg-red-600"
      : type === "success"
      ? "bg-green-600"
      : type === "warning"
      ? "bg-orange-500"
      : "bg-yellow-600";

  const handleKeyDown = (e) => {
    const focusable = modalRef.current?.querySelectorAll(
      'input, button, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const focusableEls = Array.from(focusable || []).filter(
      (el) => !el.disabled
    );

    if (e.key === "Tab" && focusableEls.length > 0) {
      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }

    if (e.key === "Enter") {
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === "textarea") return;

      e.preventDefault();

      if (onConfirm) onConfirm();
      else if (onClose) onClose();
    }

    if (e.key === "Escape") {
      e.preventDefault();
      onClose?.();
    }
  };

  return (
    <div className="responsive-dialog fixed inset-0 z-[9999] flex items-center justify-center bg-black/45 px-4">
      <div
        ref={modalRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={`w-full ${size} overflow-hidden rounded-2xl bg-white shadow-2xl animate-[fadeIn_.15s_ease-out] outline-none`}
      >
        <div className={`${accent} flex items-center justify-between px-4 py-3`}>
          <h3 className="text-base font-bold text-white">{title}</h3>
          <button onClick={onClose} className="text-lg font-bold text-white">
            ×
          </button>
        </div>

        <div className="max-h-[80vh] overflow-auto px-4 py-4">
          {message ? (
            <p className="whitespace-pre-line text-sm text-gray-700">
              {message}
            </p>
          ) : null}

          {children ? <div className="mt-3">{children}</div> : null}
        </div>

        {(onConfirm || showCancel) && (
          <div className="flex justify-end gap-2 border-t bg-gray-50 px-4 py-3">
            {showCancel && (
              <button
                onClick={onClose}
                className="h-10 rounded-xl border border-gray-300 bg-white px-4 font-semibold text-gray-700 transition hover:bg-gray-100"
              >
                {cancelText}
              </button>
            )}

            {onConfirm && (
              <button
                onClick={onConfirm}
                className={`h-10 rounded-xl px-4 font-semibold text-white transition hover:opacity-90 ${accent}`}
              >
                {confirmText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Toast({ open, message, type = "info" }) {
  if (!open || !message) return null;

  const tone =
    type === "error"
      ? "bg-red-600"
      : type === "success"
      ? "bg-green-600"
      : type === "warning"
      ? "bg-orange-500"
      : "bg-slate-800";

  return (
    <div className="fixed right-4 top-4 z-[9999]">
      <div
        className={`${tone} max-w-sm rounded-xl px-4 py-3 text-sm font-medium text-white shadow-xl`}
      >
        {message}
      </div>
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hr = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hr}:${min}`;
}

export default function POSActionsBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const posUserInfo = useSelector((s) => s.posUser.userInfo);
  const token = posUserInfo?.token;
  const name = posUserInfo?.username || "";
  const role = posUserInfo?.role || "";
  const location = posUserInfo?.location || "";

  const queueCount = useSelector((s) => s.orders?.queueCount ?? 0);
  const publishStatus = useSelector((s) => s.orders?.publishStatus || "idle");
  const isPublishing = publishStatus === "loading";

  const cartItems = useSelector((s) => s.cart.items || []);
  const cartTotal = useSelector((s) => s.cart.total || 0);
  const productsRefreshing = useSelector((s) => s.products?.loading || false);

  const posOrdersList = useSelector((s) => s.orders?.posOrdersList || []);
  const posOrdersListLoading = useSelector(
    (s) => s.orders?.posOrdersListLoading || false
  );
  const posOrderDetails = useSelector((s) => s.orders?.posOrderDetails || null);
  const posOrderDetailsLoading = useSelector(
    (s) => s.orders?.posOrderDetailsLoading || false
  );
  const paymentLoading = useSelector((s) => s.payment?.loading || false);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    type: "info",
  });

  const [infoModal, setInfoModal] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
  });

  const [clearCartModalOpen, setClearCartModalOpen] = useState(false);
  const [holdModalOpen, setHoldModalOpen] = useState(false);
  const [holdPhone, setHoldPhone] = useState("");
  const [holdError, setHoldError] = useState("");

  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [ordersView, setOrdersView] = useState("list");
  const [orderFilterMode, setOrderFilterMode] = useState("latest");
  const [searchPhone, setSearchPhone] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [showMultiModal, setShowMultiModal] = useState(false);
  const [showMultiPhoneModal, setShowMultiPhoneModal] = useState(false);
  const [showMultiFulfillmentModal, setShowMultiFulfillmentModal] = useState(false);
  const buyer = useSelector(state => state.cart.buyer);
  const [multiPhone, setMultiPhone] = useState("");
  const [multiFulfillmentOptions, setMultiFulfillmentOptions] = useState(null);
  const [multiUpiPaid, setMultiUpiPaid] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [multiInvoiceOpen, setMultiInvoiceOpen] = useState(false);
  const [multiInvoiceOrder, setMultiInvoiceOrder] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [userMenuPosition, setUserMenuPosition] = useState({
    right: 12,
    bottom: 64,
  });
  const userMenuButtonRef = useRef(null);

  const showToast = useCallback((message, type = "info") => {
    setToast({ open: true, message, type });
    setTimeout(() => {
      setToast((prev) => ({ ...prev, open: false }));
    }, 2500);
  }, []);

  const showInfoModal = useCallback((title, message, type = "info") => {
    setInfoModal({
      open: true,
      title,
      message,
      type,
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    setInfoModal((prev) => ({ ...prev, open: false }));
  }, []);

  const handleOpenPrinterSettings = useCallback(() => {
    setSettingsOpen(false);
    openPrinterSettingsWindow();
  }, []);

  const handleReloadApp = useCallback(() => {
    window.location.reload();
  }, []);

  const handleRefreshProducts = useCallback(async () => {
    if (!navigator.onLine) {
      showInfoModal(
        "No Network",
        "Connect to the internet to refresh products from DB.",
        "warning"
      );
      return;
    }

    const ok = await pingBackend(undefined, 2000, token);
    if (!ok) {
      showInfoModal(
        "Backend Unreachable",
        "Check the API server and try again.",
        "error"
      );
      return;
    }

    try {
      const products = await dispatch(fetchAllProductsFresh({ token })).unwrap();
      showToast(`Products refreshed from DB (${products.length}).`, "success");
    } catch (e) {
      showInfoModal(
        "Refresh Failed",
        e?.message || "Could not refresh products from DB.",
        "error"
      );
    }
  }, [dispatch, showInfoModal, showToast, token]);

  const handleOpenTopProducts = useCallback(() => {
    setSettingsOpen(false);
    navigate("/reports/top-products");
  }, [navigate]);

  const toggleUserMenu = useCallback(() => {
    const rect = userMenuButtonRef.current?.getBoundingClientRect();

    if (rect) {
      const menuWidth = 240;
      const gap = 10;
      const right = Math.max(
        8,
        window.innerWidth - Math.min(rect.right, window.innerWidth - 8)
      );
      const leftSpaceRight = Math.max(8, window.innerWidth - rect.left - menuWidth);
      const bottom = Math.max(8, window.innerHeight - rect.top + gap);

      setUserMenuPosition({
        right: right + menuWidth > window.innerWidth ? leftSpaceRight : right,
        bottom,
      });
    }

    setSettingsOpen((prev) => !prev);
  }, []);

  const handleLogout = useCallback(() => {
    dispatch(logout());
    localStorage.removeItem("posUserInfo");
    window.location.href = "/login";
  }, [dispatch]);

  const handleClearCart = useCallback(() => {
    if (cartItems.length === 0) {
      showToast("Cart already empty.", "warning");
      return;
    }

    setClearCartModalOpen(true);
  }, [cartItems, showToast]);

  const confirmClearCart = useCallback(() => {
    dispatch(clearCart());
    localStorage.removeItem("cart");
    setClearCartModalOpen(false);
    showToast("Cart cleared successfully.", "success");
  }, [dispatch, showToast]);

  const handlePublish = useCallback(async () => {
    if (!navigator.onLine) {
      showInfoModal(
        "No Network",
        "Connect to the internet to publish orders.",
        "warning"
      );
      return;
    }

    const ok = await pingBackend(undefined, 2000, token);
    if (!ok) {
      showInfoModal(
        "Backend Unreachable",
        "Check the API server and try again.",
        "error"
      );
      return;
    }

    if (!queueCount) {
      showToast("No queued orders to publish.", "warning");
      return;
    }

    try {
      const res = await dispatch(
        publishQueuedOrdersSequential({ token })
      ).unwrap();

      const message = res?.published
        ? `Published ${res.published} order(s).${
            res.failed ? ` ${res.failed} failed.` : ""
          }`
        : "No queued orders to publish.";

      showInfoModal(
        res?.published ? "Publish Complete" : "Nothing to Publish",
        message,
        res?.failed ? "warning" : "success"
      );
    } catch (e) {
      showInfoModal("Publish Failed", e?.message || String(e), "error");
    }
  }, [dispatch, queueCount, showInfoModal, showToast, token]);

  const handleHold = useCallback(() => {
    if (cartItems.length === 0) {
      showToast("Cart is empty.", "error");
      return;
    }

    setHoldPhone("");
    setHoldError("");
    setHoldModalOpen(true);
  }, [cartItems, showToast]);

  const confirmHold = useCallback(() => {
    if (!/^\d{10}$/.test(holdPhone)) {
      setHoldError("Please enter a valid 10-digit phone number.");
      return;
    }

    localStorage.setItem(
      `hold${holdPhone}`,
      JSON.stringify({ items: cartItems, total: cartTotal, buyer })
    );

    dispatch(clearCart());
    setHoldModalOpen(false);
    setHoldPhone("");
    setHoldError("");
    showToast(`Cart placed on hold for ${holdPhone}.`, "success");
  }, [holdPhone, cartItems, cartTotal, dispatch, showToast, buyer]);

  const openOrdersModal = useCallback(async () => {
    setOrdersModalOpen(true);
    setOrdersView("list");
    setOrderFilterMode("latest");
    try {
      await dispatch(fetchPOSOrders({ mode: "latest" })).unwrap();
    } catch (e) {
      showToast(e?.message || "Failed to load orders", "error");
    }
  }, [dispatch, showToast]);

  const loadOrders = useCallback(
    async (mode) => {
      try {
        setOrdersView("list");
        setOrderFilterMode(mode);

        if (mode === "latest") {
          await dispatch(fetchPOSOrders({ mode: "latest" })).unwrap();
        } else if (mode === "today") {
          await dispatch(fetchPOSOrders({ mode: "today" })).unwrap();
        } else if (mode === "phone") {
          if (!/^\d{10}$/.test(searchPhone)) {
            showToast("Enter valid 10-digit phone number.", "warning");
            return;
          }

          await dispatch(
            fetchPOSOrders({ mode: "phone", phone: searchPhone })
          ).unwrap();
        } else if (mode === "custom") {
          if (!fromDate || !toDate) {
            showToast("Select from and to dates.", "warning");
            return;
          }

          await dispatch(
            fetchPOSOrders({ mode: "custom", from: fromDate, to: toDate })
          ).unwrap();
        }
      } catch (e) {
        showToast(e?.message || "Failed to load orders", "error");
      }
    },
    [dispatch, searchPhone, fromDate, toDate, showToast]
  );

  const handleOpenOrderDetails = useCallback(
    async (id) => {
      try {
        await dispatch(fetchPOSOrderDetails(id)).unwrap();
        setOrdersView("details");
      } catch (e) {
        showToast(e?.message || "Failed to load order details", "error");
      }
    },
    [dispatch, showToast]
  );

  const handleMulti = useCallback(() => {
    if (cartItems.length === 0) {
      showToast("Cart is empty.", "error");
      return;
    }

    setMultiPhone("");
    setMultiFulfillmentOptions(null);
    setMultiUpiPaid(false);
    setShowMultiPhoneModal(true);
  }, [cartItems.length, showToast]);

  const handleMultiPhoneConfirm = useCallback(async (digits) => {
    await dispatch(preparePaymentBuyer(digits));
    setMultiPhone(digits);
    setShowMultiPhoneModal(false);
    setShowMultiFulfillmentModal(true);
  }, [dispatch]);

  const handleMultiFulfillmentConfirm = useCallback((options) => {
    setMultiFulfillmentOptions(options);
    setShowMultiFulfillmentModal(false);
    setShowMultiModal(true);
  }, []);

  const buildMultiOrderPayload = useCallback(
    (payments, customerId = null) => {
      const paymentSummary = payments
        .map((payment) => `${payment.channel} Rs. ${payment.amount.toFixed(2)}`)
        .join(" + ");
      const packingWarehouseLocation =
        multiFulfillmentOptions?.packingWarehouseLocation || "";
      const basePosLocation = posUserInfo?.location || "";
      const posLocation = packingWarehouseLocation
        ? [basePosLocation, packingWarehouseLocation].filter(Boolean).join("|")
        : basePosLocation;

      return {
        MK_order_id: Number(`${Date.now()}${Math.floor(Math.random() * 90 + 10)}`),
        user: customerId,
        shippingAddress: {
          street: "Gollavelli",
          city: "Amalapuram",
          postalCode: "533222",
          country: "India",
        },
        paymentMethod: payments.some(payment => payment.channel === "Pay Later") ? "Pay Later" : "Multi",
        paymentBreakdown: payments,
        orderItems: cartItems.map((item) => ({
          name: item.item,
          quantity: item.catalogQuantity,
          units: item.units,
          brand: item.brand,
          qty: item.qty,
          image: item.image || "",
          price: item.dprice,
          product_code: item.product_code || "",
          productId: item.id,
          brandId: item.brandId,
          financialId: item.financialId,
        })),
        itemsPrice: Number(cartTotal || 0),
        totalPrice: Number(cartTotal || 0),
        discountPercentage: 0,
        discountAmount: 0,
        phoneNo: multiPhone,
        posUserName: posUserInfo?.username || "",
        posLocation,
        source: "POS",
        ...(multiFulfillmentOptions || {}),
        remarks: `Multi payment: ${paymentSummary}`,
      };
    },
    [cartItems, cartTotal, multiFulfillmentOptions, multiPhone, posUserInfo]
  );

  const getOrCreateCustomerForMulti = useCallback(async () => {
    try {
      const customer = await dispatch(
        fetchCustomerByPhone({ phone: multiPhone, token })
      ).unwrap();
      return customer?._id || null;
    } catch {
      const customer = await dispatch(
        createCustomer({
          name: "NA",
          phone: multiPhone,
          address: {
            street: "Gollavelli",
            city: "Amalapuram",
            postalCode: "533222",
          },
          token,
        })
      ).unwrap();
      return customer?._id || null;
    }
  }, [dispatch, multiPhone, token]);

  const getPaymentRedirectUrl = (result) =>
    result?.redirect_url ||
    result?.paymentUrl ||
    result?.webUrl ||
    result?.data?.payment_links?.web ||
    null;

  const handlePayMultiUpi = useCallback(
    async ({ payments }) => {
      const upiAmount = payments.reduce((sum, payment) => {
        const channel = String(payment.channel || "").toUpperCase();
        const isUpi = channel.includes("UPI") || channel.includes("QR");
        return isUpi ? sum + Number(payment.amount || 0) : sum;
      }, 0);

      if (upiAmount <= 0) {
        showToast("Enter UPI / QR amount before payment.", "warning");
        return;
      }

      try {
        const customerId = await getOrCreateCustomerForMulti();
        const orderPayload = buildMultiOrderPayload(payments, customerId);
        const createdOrder = await dispatch(
          createOrder({
            payload: orderPayload,
            token,
            cartItems,
            updateStock: false,
          })
        ).unwrap();

        const orderId = createdOrder?._id;
        if (!orderId) {
          throw new Error("Order created but order ID missing");
        }

        localStorage.setItem(
          "upiInvoiceSnapshot",
          JSON.stringify({
            orderId,
            mkOrderId: createdOrder?.MK_order_id || orderPayload.MK_order_id,
            items: cartItems.map((item) => ({
              item: item.item,
              name: item.item,
              catalogQuantity: item.catalogQuantity,
              quantity: item.catalogQuantity,
              units: item.units,
              brand: item.brand,
              qty: item.qty,
              image: item.image || "",
              dprice: item.dprice,
              price: item.dprice,
              product_code: item.product_code || "",
              id: item.id,
              productId: item.id,
              brandId: item.brandId,
              financialId: item.financialId,
              stock: item.stock,
            })),
            total: Number(createdOrder?.totalPrice || cartTotal || 0),
            totalPrice: Number(createdOrder?.totalPrice || cartTotal || 0),
            totalQty: cartItems.reduce((sum, item) => sum + Number(item.qty || 0), 0),
            totalDiscount: 0,
            phone: multiPhone,
            paymentMethod: "Multi",
            paymentBreakdown: payments,
            datetime: createdOrder?.createdAt || new Date().toISOString(),
            posUserName: posUserInfo?.username || "",
            posLocation: orderPayload.posLocation || "",
            source: "POS",
          })
        );

        const paymentResult = await dispatch(
          initiateDeliveryPayment({
            customerId: multiPhone,
            order_id: orderId,
            amount: upiAmount,
          })
        ).unwrap();

        const redirectUrl = getPaymentRedirectUrl(paymentResult);
        if (!redirectUrl) {
          throw new Error("Payment URL not received");
        }

        setMultiUpiPaid(true);
        window.location.href = redirectUrl;
      } catch (e) {
        showToast(e?.message || "Failed to start UPI payment.", "error");
      }
    },
    [
      buildMultiOrderPayload,
      cartTotal,
      cartItems,
      dispatch,
      getOrCreateCustomerForMulti,
      multiPhone,
      posUserInfo,
      showToast,
      token,
    ]
  );

  const handleConfirmMultiPayment = useCallback(
    async ({ payments, paidAmount, pendingAmount }) => {
      const orderPayload = buildMultiOrderPayload(payments);

      try {
        const result = await dispatch(
          queueOrder({
            payload: orderPayload,
            token,
            cartItems,
            phone: multiPhone,
          })
        ).unwrap();

        const orderId =
          result?._id ||
          result?.id ||
          result?._localId ||
          result?.orderId ||
          "";

        setMultiInvoiceOrder({
          catalogSource: result?.catalogSource,
          _id: result?._id || undefined,
          id: orderId,
          orderId,
          items: cartItems,
          total: Number(cartTotal || 0),
          totalPrice: Number(cartTotal || 0),
          totalQty: cartItems.reduce((sum, item) => sum + Number(item.qty || 0), 0),
          totalDiscount: 0,
          datetime: result?.createdAt || new Date().toISOString(),
          phone: multiPhone,
          paymentMethod: "Multi",
          paymentBreakdown: payments,
          paidAmount,
          pendingAmount,
          posUserName: posUserInfo?.username || "",
          posLocation: orderPayload.posLocation || "",
          fulfillment: multiFulfillmentOptions,
          source: "POS",
        });

        setShowMultiModal(false);
        setMultiPhone("");
        setMultiFulfillmentOptions(null);
        dispatch(fetchLatestOrders());
        dispatch(clearCart());
        dispatch(fetchAllProducts(token));
        setMultiInvoiceOpen(true);
        showToast(pendingAmount > 0 ? "Order created. Balance recorded in Pending Payments." : buyer ? "Order created and stock updated." : "Payment order queued.", "success");
      } catch (e) {
        showToast(e?.message || "Failed to create multi payment order.", "error");
      }
    },
    [
      buildMultiOrderPayload,
      buyer,
      cartTotal,
      cartItems,
      dispatch,
      multiFulfillmentOptions,
      multiPhone,
      posUserInfo,
      showToast,
      token,
    ]
  );

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.repeat) return;

      const key = e.key.toLowerCase();
      const run = (action) => {
        e.preventDefault();
        e.stopPropagation();
        action();
      };

      if (!e.altKey) {
        if (key === "f3") {
          run(handleHold);
        } else if (key === "f4") {
          run(handleClearCart);
        } else if (key === "f5") {
          run(openOrdersModal);
        } else if (key === "f7") {
          run(() => {
            if (!isPublishing && queueCount && navigator.onLine) {
              handlePublish();
            }
          });
        } else if (key === "f8") {
          run(handleMulti);
        } else if (key === "f9") {
          run(handleLogout);
        }
        return;
      }

      if (key === "p") {
        run(() => {
          if (!isPublishing && queueCount && navigator.onLine) {
          handlePublish();
          }
        });
      } else if (key === "h") {
        run(handleHold);
      } else if (key === "c") {
        run(handleClearCart);
      } else if (key === "o") {
        run(openOrdersModal);
      } else if (key === "m") {
        run(handleMulti);
      } else if (key === "l") {
        run(handleLogout);
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [
    isPublishing,
    queueCount,
    handlePublish,
    handleHold,
    handleClearCart,
    openOrdersModal,
    handleMulti,
    handleLogout,
  ]);

  const baseBtn =
    "rounded-xl font-bold transition active:translate-y-[1px] whitespace-nowrap";
  const orangeBtn =
    "bg-[#ff8a00] text-white border border-[#FFD700] hover:bg-[#e57b00]";
  const mobileBtn = "h-10 px-3 text-xs shrink-0";
  const desktopBtn = "w-full h-11 text-sm";

  const ordersTotalAmount = (posOrdersList || []).reduce(
    (sum, order) => sum + getOrderDiscountSummary(order).totalAfterDiscount,
    0
  );
  const posOrderDetailsItemsTotal = (posOrderDetails?.items || []).reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );
  const posOrderDetailsDiscount = getOrderDiscountSummary(
    posOrderDetails || {},
    posOrderDetailsItemsTotal || undefined
  );

  const UserDropdown = () => (
    <div
      className="fixed z-[2147483647] w-60 rounded-2xl border border-gray-200 bg-white p-2 shadow-2xl"
      style={{
        right: `${userMenuPosition.right}px`,
        bottom: `${userMenuPosition.bottom}px`,
      }}
    >
      <button
        onClick={handleOpenTopProducts}
        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-semibold text-gray-700 hover:bg-yellow-50"
      >
        <BarChart3 size={16} className="shrink-0" />
        <span className="min-w-0 flex-1 whitespace-nowrap">Top Products</span>
      </button>
      <button
        onClick={handleOpenPrinterSettings}
        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-semibold text-gray-700 hover:bg-blue-50"
      >
        <Settings size={16} className="shrink-0" />
        <span className="min-w-0 flex-1 whitespace-nowrap">
          Printer settings
        </span>
      </button>
      <button
        onClick={handleReloadApp}
        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-semibold text-gray-700 hover:bg-slate-50"
      >
        <RefreshCw size={16} className="shrink-0" />
        <span className="min-w-0 flex-1 whitespace-nowrap">Reload app</span>
      </button>
      <button
        onClick={handleLogout}
        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-semibold text-red-600 hover:bg-red-50"
      >
        <LogOut size={16} className="shrink-0" />
        <span className="min-w-0 flex-1 whitespace-nowrap">Logout</span>
      </button>
    </div>
  );

  return (
    <>
      <Toast open={toast.open} message={toast.message} type={toast.type} />

      <AppModal
        open={infoModal.open}
        title={infoModal.title}
        message={infoModal.message}
        type={infoModal.type}
        onClose={closeInfoModal}
        confirmText="OK"
      />

      <AppModal
        open={clearCartModalOpen}
        title="Clear Entire Cart"
        message="Are you sure you want to remove all items from the cart?"
        type="warning"
        onClose={() => setClearCartModalOpen(false)}
        onConfirm={confirmClearCart}
        confirmText="Clear Cart"
        cancelText="Cancel"
        showCancel
      />

      <AppModal
        open={holdModalOpen}
        title="Hold Order"
        type="info"
        onClose={() => {
          setHoldModalOpen(false);
          setHoldPhone("");
          setHoldError("");
        }}
        onConfirm={confirmHold}
        confirmText="Save Hold"
        cancelText="Cancel"
        showCancel
      >
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
            Customer Phone Number
          </label>
          <input
            autoFocus
            type="tel"
            inputMode="numeric"
            maxLength={10}
            value={holdPhone}
            onChange={(e) => {
              const onlyDigits = e.target.value.replace(/\D/g, "");
              setHoldPhone(onlyDigits);
              if (holdError) setHoldError("");
            }}
            placeholder="Enter 10-digit phone number"
            className="h-11 w-full rounded-xl border border-gray-300 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
          />
          {holdError ? (
            <p className="text-sm font-medium text-red-600">{holdError}</p>
          ) : null}
        </div>
      </AppModal>

      <AppModal
        open={ordersModalOpen}
        title={ordersView === "list" ? "Orders" : "Order Details"}
        type="info"
        onClose={() => {
          setOrdersModalOpen(false);
          setOrdersView("list");
          setInvoiceOpen(false);
        }}
        size="max-w-6xl"
      >
        {ordersView === "list" ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => loadOrders("latest")}
                className={`h-10 rounded-xl border px-4 text-sm font-semibold ${
                  orderFilterMode === "latest"
                    ? "border-indigo-600 bg-indigo-600 text-white"
                    : "border-gray-300 bg-white text-gray-700"
                }`}
              >
                Last 10 Orders
              </button>

              <button
                onClick={() => loadOrders("today")}
                className={`h-10 rounded-xl border px-4 text-sm font-semibold ${
                  orderFilterMode === "today"
                    ? "border-indigo-600 bg-indigo-600 text-white"
                    : "border-gray-300 bg-white text-gray-700"
                }`}
              >
                Today Orders
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="mb-2 text-sm font-bold text-gray-700">
                  Search by Phone
                </div>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    maxLength={10}
                    value={searchPhone}
                    onChange={(e) =>
                      setSearchPhone(e.target.value.replace(/\D/g, ""))
                    }
                    placeholder="10-digit phone"
                    className="h-10 flex-1 rounded-lg border border-gray-300 px-3 text-sm"
                  />
                  <button
                    onClick={() => loadOrders("phone")}
                    className="h-10 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white"
                  >
                    Search
                  </button>
                </div>
              </div>

              <div className="rounded-xl border bg-gray-50 p-3 md:col-span-2">
                <div className="mb-2 text-sm font-bold text-gray-700">
                  Search by Custom Dates
                </div>
                <div className="flex flex-col gap-2 md:flex-row">
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="h-10 rounded-lg border border-gray-300 px-3 text-sm"
                  />
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="h-10 rounded-lg border border-gray-300 px-3 text-sm"
                  />
                  <button
                    onClick={() => loadOrders("custom")}
                    className="h-10 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white"
                  >
                    Get Orders
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-xl border bg-slate-50 p-3">
              <div className="flex flex-wrap gap-4 text-sm font-semibold text-gray-700">
                <div>
                  <span className="text-gray-500">Logged in as:</span>{" "}
                  {name || "-"}
                </div>
                <div>
                  <span className="text-gray-500">Role:</span> {role || "-"}
                </div>
                <div>
                  <span className="text-gray-500">Location:</span>{" "}
                  {location || "-"}
                </div>
              </div>
            </div>

            <div className="overflow-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left">S.No</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Order ID</th>
                    <th className="px-3 py-2 text-left">Source</th>
                    <th className="px-3 py-2 text-left">POS User</th>
                    <th className="px-3 py-2 text-left">Location</th>
                    <th className="px-3 py-2 text-left">
                      Customer Phone Number
                    </th>
                    <th className="px-3 py-2 text-left">Paid</th>
                    <th className="px-3 py-2 text-left">Mode</th>
                    <th className="px-3 py-2 text-right">Order Amount</th>
                  </tr>
                </thead>

                <tbody>
                  {posOrdersListLoading ? (
                    <tr>
                      <td
                        colSpan="10"
                        className="px-3 py-6 text-center text-gray-500"
                      >
                        Loading orders...
                      </td>
                    </tr>
                  ) : posOrdersList.length === 0 ? (
                    <tr>
                      <td
                        colSpan="10"
                        className="px-3 py-6 text-center text-gray-500"
                      >
                        No orders found.
                      </td>
                    </tr>
                  ) : (
                    posOrdersList.map((order, index) => {
                      const discount = getOrderDiscountSummary(order);

                      return (
                      <tr
                        key={order._id}
                        onClick={() => handleOpenOrderDetails(order._id)}
                        className="cursor-pointer border-t hover:bg-yellow-50"
                      >
                        <td className="px-3 py-2">{index + 1}</td>
                        <td className="px-3 py-2">
                          {formatDateTime(order.createdAt)}
                        </td>
                        <td className="px-3 py-2">
                          {order.MK_order_id || order._id}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-bold ${
                              order.source === "ONLINE"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-orange-100 text-orange-700"
                            }`}
                          >
                            {order.source || "-"}
                          </span>
                        </td>
                        <td className="px-3 py-2">{order.posUserName || "-"}</td>
                        <td className="px-3 py-2">{order.posLocation || "-"}</td>
                        <td className="px-3 py-2">{order.phoneNo || "-"}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-bold ${
                              order.isPaid
                                ? "bg-green-100 text-green-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {order.isPaid ? "Paid" : "Unpaid"}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {order.paymentMethod || "-"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          ₹{discount.totalAfterDiscount.toFixed(2)}
                          {discount.discountAmount > 0 ? (
                            <div className="text-xs font-semibold text-green-700">
                              Disc ₹{discount.discountAmount.toFixed(2)}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>

                <tfoot>
                  <tr className="border-t bg-yellow-100 font-bold">
                    <td colSpan="9" className="px-3 py-3 text-right">
                      Total Amount
                    </td>
                    <td className="px-3 py-3 text-right">
                      ₹{ordersTotalAmount.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  setInvoiceOpen(false);
                  setOrdersView("list");
                }}
                className="h-10 rounded-xl bg-gray-200 px-4 text-sm font-semibold text-gray-800"
              >
                Back
              </button>
            </div>

            {posOrderDetailsLoading ? (
              <div className="py-10 text-center text-gray-500">
                Loading order details...
              </div>
            ) : posOrderDetails ? (
              <>
                <div className="mb-3 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setInvoiceOpen(true)}
                    className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                  >
                    Share WhatsApp / Print
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-3 rounded-xl border bg-slate-50 p-3 md:grid-cols-2">
                  <div className="text-sm font-semibold">
                    <span className="text-gray-500">Order ID:</span>{" "}
                    {posOrderDetails.MK_order_id || posOrderDetails._id}
                  </div>
                  <div className="text-sm font-semibold">
                    <span className="text-gray-500">Phone Number:</span>{" "}
                    {posOrderDetails.phoneNo || "-"}
                  </div>
                  <div className="text-sm font-semibold">
                    <span className="text-gray-500">Source:</span>{" "}
                    {posOrderDetails.source || "-"}
                  </div>
                  <div className="text-sm font-semibold">
                    <span className="text-gray-500">POS User:</span>{" "}
                    {posOrderDetails.posUserName || "-"}
                  </div>
                  <div className="text-sm font-semibold">
                    <span className="text-gray-500">Billing location:</span>{" "}
                    {posOrderDetails.posLocation || "-"}
                  </div>
                  {posOrderDetails.destinationOutletLocation && <div className="text-sm font-semibold">
                    <span className="text-gray-500">Destination outlet:</span>{" "}{posOrderDetails.destinationOutletLocation}
                  </div>}
                  <div className="text-sm font-semibold">
                    <span className="text-gray-500">Created At:</span>{" "}
                    {formatDateTime(posOrderDetails.createdAt)}
                  </div>
                </div>

                <div className="overflow-auto rounded-xl border">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-3 py-2 text-left">S.No</th>
                        <th className="px-3 py-2 text-left">Item</th>
                        <th className="px-3 py-2 text-left">Weight</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-right">Price/oneqty</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      {(posOrderDetails.items || []).map((item, index) => (
                        <tr key={item.sNo || index} className="border-t">
                          <td className="px-3 py-2">{item.sNo || index + 1}</td>
                          <td className="px-3 py-2">{item.item || "-"}</td>
                          <td className="px-3 py-2">{item.weight || "-"}</td>
                          <td className="px-3 py-2 text-right">
                            {item.qty || 0}
                          </td>
                          <td className="px-3 py-2 text-right">
                            ₹{Number(item.pricePerQty || 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            ₹{Number(item.amount || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>

                    <tfoot>
                      {posOrderDetailsDiscount.discountAmount > 0 ? (
                        <tr className="border-t bg-green-50 font-semibold text-green-700">
                          <td colSpan="5" className="px-3 py-2 text-right">
                            Discount
                          </td>
                          <td className="px-3 py-2 text-right">
                            ₹{posOrderDetailsDiscount.discountAmount.toFixed(2)}
                          </td>
                        </tr>
                      ) : null}
                      <tr className="border-t bg-yellow-50 font-bold">
                        <td colSpan="5" className="px-3 py-3 text-right">
                          Total Amount
                        </td>
                        <td className="px-3 py-3 text-right">
                          ₹{posOrderDetailsDiscount.totalAfterDiscount.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            ) : (
              <div className="py-10 text-center text-gray-500">
                No details found.
              </div>
            )}
          </div>
        )}
      </AppModal>

      <InvoiceShareModal
        open={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        order={{
          ...(posOrderDetails || {}),
          items: posOrderDetails?.items || [],
          total: posOrderDetailsDiscount.totalAfterDiscount,
          totalPrice: posOrderDetailsDiscount.totalAfterDiscount,
          discountAmount: posOrderDetailsDiscount.discountAmount,
          discountPercentage: posOrderDetailsDiscount.discountPercentage,
          phone: posOrderDetails?.phoneNo || "",
          paymentMethod: posOrderDetails?.paymentMethod || "-",
        }}
        phone={posOrderDetails?.phoneNo || ""}
        title="POS Order Invoice"
      />

      <InvoiceShareModal
        open={multiInvoiceOpen}
        onClose={() => setMultiInvoiceOpen(false)}
        order={multiInvoiceOrder}
        phone={multiInvoiceOrder?.phone || ""}
        title="Payment Invoice"
      />

      {showMultiPhoneModal && (
        <PhoneModal
          stakeholderType={buyer?.stakeholderType}
          token={token}
          onCancel={() => {
            setShowMultiPhoneModal(false);
            setMultiPhone("");
          }}
          onConfirm={handleMultiPhoneConfirm}
        />
      )}

      <OrderFulfillmentModal
        open={showMultiFulfillmentModal}
        onCancel={() => {
          setShowMultiFulfillmentModal(false);
          setMultiPhone("");
          setMultiFulfillmentOptions(null);
        }}
        onConfirm={handleMultiFulfillmentConfirm}
        confirmLabel="Continue to Payment"
      />

      {showMultiModal && (
        <MultiPaymentModal
          total={Number(cartTotal || 0)}
          onCancel={() => {
            setShowMultiModal(false);
            setMultiPhone("");
            setMultiFulfillmentOptions(null);
            setMultiUpiPaid(false);
          }}
          onConfirm={handleConfirmMultiPayment}
          onPayUpi={handlePayMultiUpi}
          upiPaid={multiUpiPaid}
          paymentLoading={paymentLoading}
        />
      )}

      <div className="h-full w-full bg-white">
        <div className="border-t bg-white px-2 py-2 md:hidden">
          <div className="no-scrollbar flex items-center gap-2 overflow-x-auto">
            <div className="shrink-0">
              <div className="rounded-full bg-[#FFD700] p-[2px]">
                <img
                  src={logo}
                  alt="ManaKirana"
                  className="h-10 w-10 rounded-full object-cover ring-2 ring-[#FFD700]"
                  draggable="false"
                />
              </div>
            </div>

            <button
              onClick={handlePublish}
              disabled={isPublishing || !queueCount || !navigator.onLine}
              className={[
                baseBtn,
                mobileBtn,
                "shrink-0 text-white",
                queueCount && !isPublishing && navigator.onLine
                  ? "border border-indigo-300 bg-indigo-600 hover:bg-indigo-700"
                  : "cursor-not-allowed border border-gray-300 bg-gray-400",
              ].join(" ")}
              title="Shortcut: F7 or Alt + P"
            >
              {isPublishing ? "Publishing…" : "Publish"}
              {queueCount > 0 && (
                <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white px-1 text-[8px] text-indigo-700">
                  {queueCount}
                </span>
              )}
            </button>

            <button
              onClick={handleRefreshProducts}
              disabled={productsRefreshing || !navigator.onLine}
              className={[
                baseBtn,
                mobileBtn,
                productsRefreshing || !navigator.onLine
                  ? "cursor-not-allowed border border-gray-300 bg-gray-400 text-white"
                  : "border border-emerald-300 bg-emerald-600 text-white hover:bg-emerald-700",
              ].join(" ")}
              title="Reload products from DB"
            >
              {productsRefreshing ? "Refreshing..." : "Refresh"}
            </button>

            <StakeholderActionButton className="min-h-10 min-w-[76px] shrink-0" />

            <button
              onClick={handleHold}
              className={[baseBtn, orangeBtn, mobileBtn].join(" ")}
              title="Shortcut: F3 or Alt + H"
            >
              Hold
            </button>

            <button
              onClick={handleClearCart}
              className={[
                baseBtn,
                mobileBtn,
                "bg-red-600 text-white border border-red-300 hover:bg-red-700",
              ].join(" ")}
              title="Shortcut: F4 or Alt + C"
            >
              Clear
            </button>

            <button
              onClick={openOrdersModal}
              className={[baseBtn, orangeBtn, mobileBtn].join(" ")}
              title="Shortcut: F5 or Alt + O"
            >
              Orders
            </button>

            <button
              onClick={handleMulti}
              className={[baseBtn, orangeBtn, mobileBtn].join(" ")}
              title="Shortcut: F8 or Alt + M"
            >
              Payment
            </button>

            <POSDispatchButtons
              buttonClass={[baseBtn, orangeBtn, mobileBtn].join(" ")}
            />

            <div className="relative shrink-0">
              <button
                ref={userMenuButtonRef}
                onClick={toggleUserMenu}
                className="inline-flex h-10 items-center gap-2 rounded-xl bg-slate-700 px-3 text-xs font-bold text-white hover:bg-slate-800"
                title="User menu"
              >
                <User size={15} />
                {name || "User"}
              </button>

              {settingsOpen && <UserDropdown />}
            </div>
          </div>
        </div>

        <div className="hidden h-full w-full flex-col bg-[#f9fafb] md:flex">
          <div className="shrink-0 border-b bg-white px-2 py-3">
            <div className="flex items-center justify-center">
              <div className="rounded-full bg-[#FFD700] p-[2px]">
                <img
                  src={logo}
                  alt="ManaKirana"
                  className="h-16 w-16 rounded-full object-cover ring-2 ring-[#FFD700]"
                  draggable="false"
                />
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-1.5 px-2 py-2">
            <button
              onClick={handlePublish}
              disabled={isPublishing || !queueCount || !navigator.onLine}
              className={[
                "h-9 w-full rounded-lg border text-xs font-semibold text-white transition",
                queueCount && !isPublishing && navigator.onLine
                  ? "border-slate-300 bg-slate-500 hover:bg-slate-600"
                  : "cursor-not-allowed border-gray-300 bg-gray-400",
              ].join(" ")}
              title="Shortcut: F7 or Alt + P"
            >
              <div className="flex items-center justify-center gap-1">
                <span>{isPublishing ? "Publishing…" : "Publish"}</span>
                {queueCount > 0 && (
                  <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-white px-1 text-[7px] text-slate-700">
                    {queueCount}
                  </span>
                )}
              </div>
            </button>

            <button
              onClick={handleRefreshProducts}
              disabled={productsRefreshing || !navigator.onLine}
              className={[
                baseBtn,
                desktopBtn,
                "h-9 text-xs",
                productsRefreshing || !navigator.onLine
                  ? "cursor-not-allowed border border-gray-300 bg-gray-400 text-white"
                  : "border border-emerald-300 bg-emerald-600 text-white hover:bg-emerald-700",
              ].join(" ")}
              title="Reload products from DB"
            >
              {productsRefreshing ? "Refreshing..." : "Refresh"}
            </button>

            <StakeholderActionButton className="w-full min-h-10" />

            <button
              onClick={handleHold}
              className={[baseBtn, orangeBtn, "h-9 text-xs", desktopBtn].join(
                " "
              )}
              title="Shortcut: F3 or Alt + H"
            >
              Hold
            </button>

            <button
              onClick={handleClearCart}
              className={[
                baseBtn,
                desktopBtn,
                "h-9 text-xs bg-red-600 text-white border border-red-300 hover:bg-red-700",
              ].join(" ")}
              title="Shortcut: F4 or Alt + C"
            >
              Clear Cart
            </button>

            <button
              onClick={openOrdersModal}
              className={[baseBtn, orangeBtn, "h-9 text-xs", desktopBtn].join(
                " "
              )}
              title="Shortcut: F5 or Alt + O"
            >
              Orders
            </button>

            <button
              onClick={handleMulti}
              className={[baseBtn, orangeBtn, "h-9 text-xs", desktopBtn].join(
                " "
              )}
              title="Shortcut: F8 or Alt + M"
            >
              Payment
            </button>

            <POSDispatchButtons
              buttonClass={[baseBtn, orangeBtn, "h-9 text-xs", desktopBtn].join(
                " "
              )}
            />
          </div>

          <div className="shrink-0 border-t bg-white px-2 py-3">
            <div className="relative mt-3">
              <button
                ref={userMenuButtonRef}
                onClick={toggleUserMenu}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-700 px-2 text-sm font-bold text-white transition hover:bg-slate-800"
              >
                <User size={17} />
                <span className="min-w-0 truncate">{name || "User"}</span>
              </button>
              <div className="mt-1 truncate text-center text-xs text-gray-600">
                {role || "-"} {location ? `• ${location}` : ""}
              </div>

              {settingsOpen && <UserDropdown />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
